<template>
  <b-modal ref="modal" size="xl" hide-footer centered no-close-on-backdrop :title="modalTitle">
    <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
      <CForm @submit.prevent="handleSubmit(submit)">
        <CRow>
          <CCol md="6" class="form-field">
            <label>{{ $t("holiday_setting.field_title") }} <span class="require">*</span></label>
            <ValidationProvider rules="required|max:255" v-slot="{ errors }" :name="$t('holiday_setting.field_title')">
              <b-form-input v-model="form.title" />
              <span class="require">{{ errors[0] || apiErrors.title }}</span>
            </ValidationProvider>
          </CCol>
          <CCol md="6" class="form-field">
            <label>{{ $t("holiday_setting.field_description") }}</label>
            <ValidationProvider rules="max:255" v-slot="{ errors }" :name="$t('holiday_setting.field_description')">
              <b-form-input v-model="form.description" />
              <span class="require">{{ errors[0] || apiErrors.description }}</span>
            </ValidationProvider>
          </CCol>
        </CRow>
        <label class="mt-2">{{ $t("holiday_setting.details") }}</label>
        <HolidaySettingDetailRow
          v-for="(row, index) in form.details"
          :key="row.uid"
          v-model="form.details[index]"
          :work-times="workTimes"
          :errors="rowErrors(index)"
          :removable="form.details.length > 1"
          @remove="removeRow(index)"
        />
        <div class="btn-add-new">
          <label @click="addRow">{{ "+ " + $t("holiday_setting.add_row") }}</label>
        </div>
        <CRow class="d-flex justify-content-end mr-1">
          <b-button type="submit" class="btn btn-success mt-3">{{ $t("holiday_setting.save") }}</b-button>
          <span class="mr-2"></span>
          <b-button class="mt-3" variant="outline-danger" @click="hide">{{ $t("holiday_setting.cancel") }}</b-button>
        </CRow>
      </CForm>
    </ValidationObserver>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import HolidaySettingDetailRow from "./HolidaySettingDetailRow.vue";
import { newRow, fromApi, toPayload } from "./holiday-setting-detail-helpers";

let uidSeed = 0;
const withUid = row => ({ ...row, uid: ++uidSeed });

// Create / edit a holiday setting with its detail rows. Emits "saved" after success.
export default {
  name: "HolidaySettingFormModal",
  components: { ValidationProvider, ValidationObserver, HolidaySettingDetailRow },
  data() {
    return {
      id: null,
      form: { title: "", description: "", details: [] },
      // Flat API 422 messages: { "title": "...", "details.0.day_off": "..." }
      apiErrors: {}
    };
  },
  computed: {
    ...mapGetters({ workTimes: "holiday-setting/workTimes" }),
    modalTitle() {
      return this.id ? this.$t("holiday_setting.edit") : this.$t("holiday_setting.create");
    }
  },
  methods: {
    ...mapActions("layout", ["setLoading"]),
    ...mapActions("holiday-setting", [
      "fetchWorkTimes",
      "getHolidaySetting",
      "createHolidaySetting",
      "updateHolidaySetting"
    ]),
    async open(id = null) {
      this.id = id;
      this.apiErrors = {};
      this.setLoading(true);
      try {
        await this.fetchWorkTimes();
        if (id) {
          const setting = await this.getHolidaySetting(id);
          this.form = {
            title: setting.title || "",
            description: setting.description || "",
            details: (setting.detail || []).map(detail => withUid(fromApi(detail)))
          };
        } else {
          this.form = { title: "", description: "", details: [withUid(newRow())] };
        }
        this.$refs.modal.show();
      } catch (e) {
        // Error toast already shown by axios interceptor
      } finally {
        this.setLoading(false);
      }
    },
    hide() {
      this.$refs.modal.hide();
    },
    addRow() {
      this.form.details.push(withUid(newRow()));
    },
    // API errors are keyed by row index -> drop them when indexes shift
    removeRow(index) {
      this.form.details.splice(index, 1);
      this.apiErrors = {};
    },
    rowErrors(index) {
      const prefix = `details.${index}.`;
      return Object.keys(this.apiErrors)
        .filter(key => key.startsWith(prefix))
        .reduce((acc, key) => ({ ...acc, [key.slice(prefix.length)]: this.apiErrors[key] }), {});
    },
    async submit() {
      const data = {
        title: this.form.title,
        description: this.form.description,
        details: this.form.details.map(toPayload)
      };
      this.apiErrors = {};
      this.setLoading(true);
      try {
        if (this.id) {
          await this.updateHolidaySetting({ id: this.id, data });
        } else {
          await this.createHolidaySetting(data);
        }
        const message = this.id ? "holiday_setting.update_success" : "holiday_setting.create_success";
        this.$toast(this.$t(message), this.modalTitle, 2000, "b-toaster-top-center", "success");
        this.hide();
        // Pass year of first day off so the list can switch its year filter to show the record
        const firstDayOff = data.details.map(detail => detail.day_off).sort()[0];
        this.$emit("saved", Number(String(firstDayOff).slice(0, 4)));
      } catch (error) {
        const messages = error.response && error.response.status === 422
          ? error.response.data.error.message : {};
        this.apiErrors = Object.keys(messages || {}).reduce(
          (acc, key) => ({ ...acc, [key]: messages[key][0] }), {}
        );
      } finally {
        this.setLoading(false);
      }
    }
  }
};
</script>

<style scoped>
.form-field {
  margin-bottom: 0.5rem;
}
label {
  font-weight: 600;
}
.require {
  color: red;
}
.btn-add-new label {
  color: #f6ab5f;
  font-size: 1rem;
  cursor: pointer;
}
</style>
